import { getStatusLabel } from '../../../status-labels';
import type { BillStatus, BillType } from '../../../types/bills';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import type { LotStatus } from './lot';

/**
 * A PURCHASE ORDER PRODUCT, edited from the payable that bills it (`updateBillOrderLineItem`).
 *
 *   view ───────────────────────────────────────────────────────▶ allow
 *   not a payable, or the product is not on it ──────────────────▶ NOT_ON_PAYABLE
 *   payable ∉ DRAFT · OPEN · PAID_PARTIAL · PAID_FULL ────────────▶ PAYABLE_LOCKED
 *   closed period · closed reconciliation ────────────────────────▶ PERIOD_CLOSED · RECONCILED
 *   add · delete · perform ──────────────────────────────────────▶ ACTION_NOT_APPLICABLE
 *   edit buyPrice ───────────────────────────────────────────────▶ allow
 *   edit unitsReceived · weightReceived ── lot OPEN | MID_RECEIVING ▶ allow
 *
 * The payable page's own Edit rule (BillHeader `canEdit`), as on v2026.10.08. The order's status
 * window, its used-lot lock and its open-payable lock answer for the ORDER pages only; this
 * door replaces them for the payable (TAS-3660). Received units never drop below what the lot
 * has already supplied — the manager checks that floor against the lot's usage.
 */
export type BillOrderLineItemFacts = {
  billType: BillType;
  billStatus: BillStatus;
  /** The bill's invoice date falls before the organization's books-closed date. */
  isPeriodClosed: boolean;
  /** A CLOSED account reconciliation holds one of the bill's ledger entries. */
  isReconciliationClosed: boolean;
  /** The product's order is attached to this bill. */
  isOnBill: boolean;
  /** The lot the product received into; null when it holds none (sold through, not yet received). */
  lotStatus: LotStatus | null;
};

export const BILL_ORDER_LINE_ITEM_FIELDS = ['buyPrice', 'unitsReceived', 'weightReceived'] as const;
export type BillOrderLineItemField = (typeof BILL_ORDER_LINE_ITEM_FIELDS)[number];

/** The statuses the payable page edits: DRAFT always, the rest behind its Edit toggle. */
export const BILL_ORDER_LINE_ITEM_EDITABLE_BILL_STATUSES: readonly BillStatus[] = [
  'DRAFT',
  'OPEN',
  'PAID_PARTIAL',
  'PAID_FULL',
];

/** A lot whose received amounts still move with the product: receiving, or open stock. */
const RECEIVED_QUANTITY_LOT_STATUSES: readonly LotStatus[] = ['MID_RECEIVING', 'OPEN'];

type LineAction = Action<BillOrderLineItemField>;

const decideEntity = (facts: BillOrderLineItemFacts, action: LineAction): Decision => {
  if (action.type === 'view') return allow();

  if (facts.billType !== 'PAYABLE' || !facts.isOnBill) {
    return deny('BILL_ORDER_LINE_ITEM_NOT_ON_PAYABLE', 'This product is not on this payable.');
  }
  if (!BILL_ORDER_LINE_ITEM_EDITABLE_BILL_STATUSES.includes(facts.billStatus)) {
    return deny(
      'BILL_ORDER_LINE_ITEM_PAYABLE_LOCKED',
      `Products are locked once the payable is ${getStatusLabel(facts.billStatus)}.`
    );
  }
  if (facts.isPeriodClosed) {
    return deny('BILL_ORDER_LINE_ITEM_PERIOD_CLOSED', 'Locked: the payable falls in a closed period.');
  }
  if (facts.isReconciliationClosed) {
    return deny('BILL_ORDER_LINE_ITEM_RECONCILED', 'Locked: the payable is reconciled.');
  }
  if (action.type !== 'edit') {
    return deny('BILL_ORDER_LINE_ITEM_ACTION_NOT_APPLICABLE', 'Not available on the payable.');
  }

  return allow();
};

const receivedQuantityRule = (facts: BillOrderLineItemFacts): Decision => {
  if (facts.lotStatus === null) {
    return deny('BILL_ORDER_LINE_ITEM_NOT_RECEIVED', 'Only a received product has received quantities.');
  }
  return RECEIVED_QUANTITY_LOT_STATUSES.includes(facts.lotStatus)
    ? allow()
    : deny('BILL_ORDER_LINE_ITEM_LOT_LOCKED', `Locked: the lot is ${getStatusLabel(facts.lotStatus)}.`);
};

export const billOrderLineItemPolicy: Policy<BillOrderLineItemFacts, BillOrderLineItemField> = definePolicy<
  BillOrderLineItemFacts,
  BillOrderLineItemField
>({
  kind: 'payable product',
  fields: BILL_ORDER_LINE_ITEM_FIELDS,
  ops: [],
  requires: {},
  entity: decideEntity,
  field: {
    unitsReceived: (facts, action) => (action.type === 'edit' ? receivedQuantityRule(facts) : allow()),
    weightReceived: (facts, action) => (action.type === 'edit' ? receivedQuantityRule(facts) : allow()),
  },
});
